<script setup>
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'

const RULES = [
  { label: 'No Cheating',   body: 'Aimbots, scripts, third-party tools — instant ban.' },
  { label: 'No Exploits',   body: 'Bug abuse, item duplication, mesh glitches — bannable.' },
  { label: 'No Toxicity',   body: 'Hate speech, slurs, witch-hunts — zero tolerance.' },
  { label: 'No Harassment', body: 'Stalking, doxxing, base griefing of new players — banned.' },
]
</script>

<template>
  <section class="fp">
    <div class="fp__container">
      <div class="fp__crest">
        <span class="fp__kicker">EVOLUXU · CODE OF CONDUCT</span>
        <HeadlineFraunces level="h2" class="fp__title">Fair <em>Play</em> Policy</HeadlineFraunces>
        <p class="fp__manifesto">
          We maintain a fair and respectful environment.
          Cheating, exploiting bugs, toxic behavior, and harassment are <em>strictly prohibited.</em>
        </p>
      </div>

      <ul class="fp__grid">
        <li v-for="rule in RULES" :key="rule.label" class="fp__rule">
          <h3 class="fp__rule-label">{{ rule.label }}</h3>
          <p class="fp__rule-body">{{ rule.body }}</p>
        </li>
      </ul>

      <div class="fp__seal">
        <span class="fp__seal-rule"></span>
        <span class="fp__seal-text">REPORT VIOLATIONS · admin@evoluxu.com</span>
        <span class="fp__seal-rule"></span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.fp {
  padding: var(--sp-24) var(--sp-12) var(--sp-12);
  position: relative;
}
.fp__container {
  max-width: var(--container-wide);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
}

.fp__crest {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);
  text-align: center;
}
.fp__kicker {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold);
}
.fp__title :deep(.headline) {
  font-size: clamp(var(--fs-44), 5vw, var(--fs-84));
}
.fp__manifesto {
  margin: 0;
  max-width: 64ch;
  font-size: clamp(var(--fs-18), 1.4vw, var(--fs-21));
  line-height: 1.6;
  color: var(--ink);
  opacity: 0.92;
}
.fp__manifesto :deep(em) {
  font-style: normal;
  color: var(--gold);
  font-weight: 500;
}

.fp__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid rgba(229, 199, 107, 0.30);
  border-bottom: 1px solid rgba(229, 199, 107, 0.30);
}
.fp__rule {
  position: relative;
  padding: var(--sp-8) var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  align-items: center;
  text-align: center;
  background: radial-gradient(
    ellipse at center,
    rgba(11, 10, 8, 0.62) 0%,
    rgba(11, 10, 8, 0.40) 45%,
    rgba(11, 10, 8, 0)    85%
  );
}
.fp__rule:not(:last-child)::after {
  content: "";
  position: absolute;
  top: var(--sp-6);
  bottom: var(--sp-6);
  right: 0;
  width: 1px;
  background: var(--ash-strong);
}
.fp__rule-label {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-21);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink);
  font-weight: 400;
}
.fp__rule-body {
  margin: 0;
  font-size: var(--fs-14);
  line-height: 1.55;
  color: var(--ink-soft);
}

.fp__seal {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-soft);
  justify-content: center;
}
.fp__seal-rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  opacity: 0.55;
  max-width: 200px;
}
.fp__seal-text { color: var(--gold); white-space: nowrap; }

@media (max-width: 1024px) {
  .fp { padding: var(--sp-16) var(--sp-6) var(--sp-8); }
  .fp__grid { grid-template-columns: repeat(2, 1fr); }
  .fp__rule:nth-child(2)::after { display: none; }
  .fp__rule:nth-child(odd):not(:last-child) { border-bottom: 1px solid var(--ash); }
  .fp__rule:nth-child(2) { border-bottom: 1px solid var(--ash); }
}
@media (max-width: 560px) {
  .fp__grid { grid-template-columns: 1fr; }
  .fp__rule { border-bottom: 1px solid var(--ash); }
  .fp__rule:last-child { border-bottom: 0; }
  .fp__rule::after { display: none !important; }
}
</style>
